"use client";

import { useEffect, useRef, useState } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import {
  MEDIAPIPE_FACE_LANDMARKER_MODEL_URL,
  MEDIAPIPE_WASM_URL,
} from "@/helper/storage";
import { MenuItem, Select } from "@mui/material";
import { HiOutlineVideoCamera } from "react-icons/hi2";
import {
  appThemedSelectMenuProps,
  appThemedSelectSx,
} from "@/helper/muiSelectTheme";
import { warmupFlaskApi } from "@/services/axios/protectedApi";

type FaceState = {
  faceCount: number;
  yaw: number | null;
  pitch: number | null;
  isInsideGuide: boolean;
  fillsGuide: boolean;
  status: string;
};

type CapturePose = "left" | "straight" | "right";

const LEFT_THRESHOLD = -20;
const RIGHT_THRESHOLD = 20;
const STRAIGHT_THRESHOLD = 8;
const DOWN_THRESHOLD = -15;
const UP_THRESHOLD = 20;
const GUIDE_RADIUS_X = 92;
const GUIDE_RADIUS_Y = 127;
const MIN_FACE_WIDTH = 115;
const MIN_FACE_HEIGHT = 135;

function getFaceGuideFit(
  landmarks: Array<{ x: number; y: number }>,
  video: HTMLVideoElement,
  viewport: HTMLDivElement | null
) {
  if (!viewport || !video.videoWidth || !video.videoHeight) {
    return { isInsideGuide: false, fillsGuide: false };
  }

  const { width, height } = viewport.getBoundingClientRect();
  const scale = Math.max(width / video.videoWidth, height / video.videoHeight);
  const offsetX = (width - video.videoWidth * scale) / 2;
  const offsetY = (height - video.videoHeight * scale) / 2;
  const points = landmarks.map(({ x, y }) => ({
    x: x * video.videoWidth * scale + offsetX,
    y: y * video.videoHeight * scale + offsetY,
  }));

  return {
    isInsideGuide: points.every(({ x, y }) => {
      const relativeX = (x - width / 2) / GUIDE_RADIUS_X;
      const relativeY = (y - height / 2) / GUIDE_RADIUS_Y;
      return relativeX ** 2 + relativeY ** 2 <= 1;
    }),
    fillsGuide:
      Math.max(...points.map(({ x }) => x)) -
        Math.min(...points.map(({ x }) => x)) >=
        MIN_FACE_WIDTH &&
      Math.max(...points.map(({ y }) => y)) -
        Math.min(...points.map(({ y }) => y)) >=
        MIN_FACE_HEIGHT,
  };
}

function captureFaceGuide(video: HTMLVideoElement, viewport: HTMLDivElement) {
  const { width, height } = viewport.getBoundingClientRect();
  const scale = Math.max(width / video.videoWidth, height / video.videoHeight);
  const offsetX = (width - video.videoWidth * scale) / 2;
  const offsetY = (height - video.videoHeight * scale) / 2;
  const radiusX = (GUIDE_RADIUS_X * 1.35) / scale;
  const radiusY = (GUIDE_RADIUS_Y * 1.35) / scale;
  const sourceX = (width / 2 - offsetX) / scale;
  const sourceY = (height / 2 - offsetY) / scale;
  const cropX = Math.max(0, sourceX - radiusX);
  const cropY = Math.max(0, sourceY - radiusY);
  const cropWidth = Math.min(video.videoWidth - cropX, radiusX * 2);
  const cropHeight = Math.min(video.videoHeight - cropY, radiusY * 2);
  const source = document.createElement("canvas");
  source.width = video.videoWidth;
  source.height = video.videoHeight;
  const sourceContext = source.getContext("2d");
  const crop = document.createElement("canvas");
  crop.width = Math.round(cropWidth);
  crop.height = Math.round(cropHeight);
  const cropContext = crop.getContext("2d");

  if (!sourceContext || !cropContext) return null;

  sourceContext.translate(source.width, 0);
  sourceContext.scale(-1, 1);
  sourceContext.drawImage(video, 0, 0, source.width, source.height);
  cropContext.drawImage(
    source,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    crop.width,
    crop.height
  );
  return crop.toDataURL("image/jpeg", 0.85);
}

const captureSteps = [
  {
    title: "Turn slightly left",
    label: "Left",
    hint: "Rotate your head toward the left mark",
    isAligned: (yaw: number) => yaw > RIGHT_THRESHOLD,
  },
  {
    title: "Look straight ahead",
    label: "Straight",
    hint: "Fill the oval and hold still",
    isAligned: (yaw: number) => Math.abs(yaw) <= STRAIGHT_THRESHOLD,
  },
  {
    title: "Turn slightly right",
    label: "Right",
    hint: "Rotate your head toward the right mark",
    isAligned: (yaw: number) => yaw < LEFT_THRESHOLD,
  },
];
const captureOrder = [1, 0, 2];

function getNextMissingCaptureStep(
  captures: (string | null)[],
  currentStep: number
) {
  for (let offset = 1; offset <= captureOrder.length; offset += 1) {
    const nextStep = (currentStep + offset) % captureOrder.length;
    if (!captures[captureOrder[nextStep]]) return nextStep;
  }
  return currentStep;
}

type ProctoringPOCProps = {
  required?: boolean;
  onComplete?: (captures: [string, string, string]) => Promise<void> | void;
  onClose?: () => void;
  onCameraUnavailable?: () => void;
};

export default function ProctoringPOC({
  required = false,
  onComplete,
  onClose,
  onCameraUnavailable,
}: ProctoringPOCProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const landmarkerRef = useRef<FaceLandmarker | null>(null);
  const processTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captureTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const captureTransitionTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const isCapturingRef = useRef(false);
  const isCompleteRef = useRef(false);
  const isManualCaptureRef = useRef(false);

  const [isModalOpen, setIsModalOpen] = useState(true);
  const [hasStartedCapture, setHasStartedCapture] = useState(false);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState("");
  const [isCheckingCameras, setIsCheckingCameras] = useState(true);
  const [cameraError, setCameraError] = useState("");
  const [cameraCheck, setCameraCheck] = useState(0);
  const [ready, setReady] = useState(false);
  const [isManualCapture, setIsManualCapture] = useState(false);
  const [visionError, setVisionError] = useState("");

  useEffect(() => {
    void warmupFlaskApi().catch(() => undefined);
  }, []);
  const [captureStep, setCaptureStep] = useState(0);
  const [manualCapturedPose, setManualCapturedPose] = useState<number | null>(
    null
  );
  const [captures, setCaptures] = useState<(string | null)[]>([
    null,
    null,
    null,
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [captureErrors, setCaptureErrors] = useState<
    Partial<Record<CapturePose, string>>
  >({});
  const [faceState, setFaceState] = useState<FaceState>({
    faceCount: 0,
    yaw: null,
    pitch: null,
    isInsideGuide: false,
    fillsGuide: false,
    status: "Initializing camera...",
  });

  const captureIndex = captureOrder[captureStep];
  const currentStep = captureSteps[captureIndex];
  const activeCaptureError =
    captureErrors[
      currentStep.label.toLowerCase() as keyof typeof captureErrors
    ] || submitError;
  const hasGoodPitch =
    faceState.pitch !== null &&
    faceState.pitch >= DOWN_THRESHOLD &&
    faceState.pitch <= UP_THRESHOLD;
  const isLookingDown =
    faceState.pitch !== null && faceState.pitch < DOWN_THRESHOLD;
  const isAligned =
    ready &&
    faceState.faceCount === 1 &&
    faceState.yaw !== null &&
    hasGoodPitch &&
    faceState.isInsideGuide &&
    faceState.fillsGuide &&
    currentStep.isAligned(faceState.yaw);
  const isComplete = captures.every(Boolean) && manualCapturedPose === null;

  useEffect(() => {
    isCompleteRef.current = isComplete;

    if (isComplete) {
      if (processTimeoutRef.current) clearTimeout(processTimeoutRef.current);
      processTimeoutRef.current = null;
    } else if (ready && !isManualCapture && !processTimeoutRef.current) {
      processFrame();
    }
  }, [isComplete, isManualCapture, ready]);

  useEffect(() => {
    if (!isModalOpen || hasStartedCapture) return;

    let cancelled = false;

    async function checkCameras() {
      setIsCheckingCameras(true);
      setCameraError("");
      const previewStream = previewVideoRef.current
        ?.srcObject as MediaStream | null;
      previewStream?.getTracks().forEach((track) => track.stop());
      if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
      setSelectedCamera("");
      let stream: MediaStream | null = null;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });
        const activeCamera = stream.getVideoTracks()[0]?.getSettings().deviceId;
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );

        if (cancelled) return;

        setCameras(videoDevices);
        setSelectedCamera((current) =>
          videoDevices.some((device) => device.deviceId === current)
            ? current
            : activeCamera &&
                videoDevices.some((device) => device.deviceId === activeCamera)
              ? activeCamera
              : videoDevices[0]?.deviceId || ""
        );
        if (!videoDevices.length) setCameraError("No camera was found.");
      } catch {
        if (!cancelled) {
          setCameras([]);
          setSelectedCamera("");
          setCameraError(
            "We couldn't access your camera. Allow camera permission and try again."
          );
          onCameraUnavailable?.();
        }
      } finally {
        stream?.getTracks().forEach((track) => track.stop());
        if (!cancelled) setIsCheckingCameras(false);
      }
    }

    void checkCameras();

    return () => {
      cancelled = true;
    };
  }, [cameraCheck, hasStartedCapture, isModalOpen]);

  useEffect(() => {
    if (!isModalOpen || hasStartedCapture || !selectedCamera) return;

    let cancelled = false;
    let stream: MediaStream | null = null;

    async function startPreview() {
      setCameraError("");

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: selectedCamera } },
          audio: false,
        });
        if (cancelled || !previewVideoRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        previewVideoRef.current.srcObject = stream;
        stream.getVideoTracks()[0].onended = () => {
          if (!cancelled) onCameraUnavailable?.();
        };
        await previewVideoRef.current.play();
      } catch {
        stream?.getTracks().forEach((track) => track.stop());
        if (!cancelled) {
          setCameraError("We couldn't start the selected camera. Try again.");
          onCameraUnavailable?.();
        }
      }
    }

    void startPreview();

    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
      if (previewVideoRef.current) previewVideoRef.current.srcObject = null;
    };
  }, [hasStartedCapture, isModalOpen, selectedCamera]);

  useEffect(() => {
    if (!isModalOpen || !hasStartedCapture || !selectedCamera) return;

    let cancelled = false;

    async function initialize() {
      let stream: MediaStream | null = null;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: selectedCamera },
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
          audio: false,
        });

        if (cancelled || !videoRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        videoRef.current.srcObject = stream;
        stream.getVideoTracks()[0].onended = () => {
          if (!cancelled) onCameraUnavailable?.();
        };
        await videoRef.current.play();
      } catch {
        if (!cancelled) {
          setFaceState((state) => ({ ...state, status: "Camera unavailable" }));
          onCameraUnavailable?.();
        }
        return;
      }

      try {
        const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_URL);
        const landmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: MEDIAPIPE_FACE_LANDMARKER_MODEL_URL,
          },
          runningMode: "VIDEO",
          numFaces: 2,
          minFaceDetectionConfidence: 0.6,
          minFacePresenceConfidence: 0.5,
          minTrackingConfidence: 0.4,
          outputFacialTransformationMatrixes: true,
        });

        if (cancelled || isManualCaptureRef.current) {
          landmarker.close();
          return;
        }

        landmarkerRef.current = landmarker;
        setReady(true);
        if (!isManualCaptureRef.current) processFrame();
      } catch {
        if (!cancelled) {
          setVisionError(
            "Automatic face guidance is unavailable. Capture each photo manually."
          );
          startManualCapture();
          setFaceState((state) => ({ ...state, status: "Manual capture" }));
        }
      }
    }

    void initialize();

    return () => {
      cancelled = true;
      if (processTimeoutRef.current) clearTimeout(processTimeoutRef.current);
      if (captureTimeoutRef.current) clearTimeout(captureTimeoutRef.current);
      if (captureTransitionTimeoutRef.current) {
        clearTimeout(captureTransitionTimeoutRef.current);
        captureTransitionTimeoutRef.current = null;
      }
      landmarkerRef.current?.close();
      landmarkerRef.current = null;
      const stream = videoRef.current?.srcObject as MediaStream | null;
      stream?.getTracks().forEach((track) => track.stop());
      setReady(false);
    };
  }, [hasStartedCapture, isModalOpen, selectedCamera]);

  useEffect(() => {
    if (isManualCapture || !isAligned || isComplete || isCapturingRef.current) {
      return;
    }

    captureTimeoutRef.current = setTimeout(captureCurrentFrame, 750);
    return () => {
      if (captureTimeoutRef.current) clearTimeout(captureTimeoutRef.current);
    };
  }, [isAligned, isComplete, captureStep, isManualCapture]);

  function processFrame() {
    if (isCompleteRef.current || isManualCaptureRef.current) return;

    processTimeoutRef.current = setTimeout(processFrame, 200);

    const video = videoRef.current;
    const landmarker = landmarkerRef.current;
    if (!video || !landmarker || video.readyState < 2) return;

    const now = performance.now();
    let result;
    try {
      result = landmarker.detectForVideo(video, now);
    } catch {
      if (processTimeoutRef.current) clearTimeout(processTimeoutRef.current);
      processTimeoutRef.current = null;
      landmarker.close();
      landmarkerRef.current = null;
      setReady(false);
      setVisionError(
        "Automatic face guidance stopped working. Capture each photo manually."
      );
      startManualCapture();
      setFaceState((state) => ({ ...state, status: "Manual capture" }));
      return;
    }
    const faceCount = result.faceLandmarks?.length ?? 0;

    if (faceCount !== 1) {
      setFaceState({
        faceCount,
        yaw: null,
        pitch: null,
        isInsideGuide: false,
        fillsGuide: false,
        status: faceCount
          ? "Multiple faces detected"
          : "Looking for your face...",
      });
      return;
    }

    const matrix = result.facialTransformationMatrixes?.[0]?.data;
    const { isInsideGuide, fillsGuide } = getFaceGuideFit(
      result.faceLandmarks[0],
      video,
      viewportRef.current
    );
    const pitch = matrix
      ? Math.atan2(-matrix[9], matrix[10]) * (180 / Math.PI)
      : null;
    const yaw = matrix
      ? Math.atan2(matrix[8], Math.sqrt(matrix[9] ** 2 + matrix[10] ** 2)) *
        (180 / Math.PI)
      : null;

    setFaceState({
      faceCount,
      yaw,
      pitch,
      isInsideGuide,
      fillsGuide,
      status: isInsideGuide
        ? "Face detected"
        : "Move your face fully inside the oval",
    });
  }

  function startManualCapture() {
    isManualCaptureRef.current = true;
    if (processTimeoutRef.current) clearTimeout(processTimeoutRef.current);
    processTimeoutRef.current = null;
    landmarkerRef.current?.close();
    landmarkerRef.current = null;
    setReady(false);
    setIsManualCapture(true);
  }

  function captureCurrentFrame() {
    const video = videoRef.current;
    const viewport = viewportRef.current;
    if (
      !video?.videoWidth ||
      !viewport ||
      isCapturingRef.current ||
      isComplete ||
      (!isManualCapture && !isAligned)
    ) {
      return;
    }

    isCapturingRef.current = true;
    const image = captureFaceGuide(video, viewport);
    if (!image) {
      isCapturingRef.current = false;
      return;
    }

    const nextCaptures = captures.map((capture, index) =>
      index === captureIndex ? image : capture
    );
    setCaptures(nextCaptures);
    setCaptureErrors((current) => ({
      ...current,
      [currentStep.label.toLowerCase() as CapturePose]: undefined,
    }));
    if (isManualCapture) {
      setManualCapturedPose(captureIndex);
      captureTransitionTimeoutRef.current = setTimeout(() => {
        setCaptureStep((step) => getNextMissingCaptureStep(nextCaptures, step));
        setManualCapturedPose(null);
        isCapturingRef.current = false;
        captureTransitionTimeoutRef.current = null;
      }, 800);
    } else {
      setTimeout(() => {
        setCaptureStep((step) => getNextMissingCaptureStep(nextCaptures, step));
        isCapturingRef.current = false;
      }, 350);
    }
  }

  function retake(index: number) {
    if (!captures[index]) return;
    if (captureTimeoutRef.current) clearTimeout(captureTimeoutRef.current);
    if (captureTransitionTimeoutRef.current) {
      clearTimeout(captureTransitionTimeoutRef.current);
      captureTransitionTimeoutRef.current = null;
    }
    isCapturingRef.current = false;
    setManualCapturedPose(null);
    setCaptureErrors((current) => ({
      ...current,
      [captureSteps[index].label.toLowerCase() as CapturePose]: undefined,
    }));
    setCaptures((current) =>
      current.map((capture, itemIndex) =>
        itemIndex === index ? null : capture
      )
    );
    setCaptureStep(captureOrder.indexOf(index));
  }

  async function continueCapture() {
    if (!isComplete || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await onComplete?.(captures as [string, string, string]);
      const stream = videoRef.current?.srcObject as MediaStream | null;
      stream?.getTracks().forEach((track) => track.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
      setIsModalOpen(false);
      onClose?.();
    } catch (error: any) {
      const responsePoseErrors = error?.response?.data?.poseErrors as
        | Partial<Record<CapturePose, string>>
        | undefined;
      const clearAllCaptures = error?.response?.data?.clearAllCaptures === true;
      const failedCaptureIndexes = responsePoseErrors
        ? captureSteps.flatMap((step, index) =>
            responsePoseErrors[
              step.label.toLowerCase() as keyof typeof responsePoseErrors
            ]
              ? [index]
              : []
          )
        : clearAllCaptures
          ? [0, 1, 2]
          : [];
      setCaptureErrors(responsePoseErrors || {});
      if (failedCaptureIndexes.length) {
        setCaptures((current) =>
          current.map((capture, index) =>
            failedCaptureIndexes.includes(index) ? null : capture
          )
        );
        setCaptureStep(captureOrder.indexOf(failedCaptureIndexes[0]));
      }
      setSubmitError(
        responsePoseErrors
          ? ""
          : error?.response?.data?.message ||
              error?.message ||
              "We could not verify your photos. Please retake them and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const status = isManualCapture
    ? "Manual capture"
    : !ready
      ? faceState.status
      : isComplete
        ? "All captures complete"
        : isAligned
          ? "Aligned — hold still"
          : faceState.faceCount !== 1
            ? faceState.status
            : isLookingDown
              ? "Raise your head to face the camera"
              : !faceState.fillsGuide
                ? "Move closer and fill the oval"
                : !faceState.isInsideGuide
                  ? "Cover your full face with the oval"
                  : "Move into the oval";
  const hint = isManualCapture
    ? "Position your face in the oval, then capture"
    : faceState.faceCount === 1 && !faceState.fillsGuide
      ? "Move closer until your face fills the oval"
      : faceState.faceCount === 1 && !faceState.isInsideGuide
        ? "Cover your full face within the oval"
        : currentStep.hint;
  const nextCaptureStep = captures.every(Boolean)
    ? null
    : getNextMissingCaptureStep(captures, captureStep);

  if (!isModalOpen) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-appBg p-4">
        <button
          className="rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-onPrimary"
          onClick={() => setIsModalOpen(true)}
        >
          Open face capture
        </button>
      </main>
    );
  }

  if (!hasStartedCapture) {
    return (
      <main className="min-h-dvh bg-appBg md:p-4">
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 md:backdrop-blur-sm">
          <section
            aria-label="Profile landmark setup"
            className="relative max-h-[calc(100dvh-2rem)] w-full max-w-[360px] overflow-y-auto rounded-2xl border border-appText/10 bg-cardBg p-6 shadow-[0_30px_70px_-24px_rgba(20,10,40,0.55)]"
          >
            {!required && (
              <button
                aria-label="Close profile landmark setup"
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-appBg/80 text-xl text-appText/60 transition hover:text-appText"
                onClick={() => {
                  setIsModalOpen(false);
                  onClose?.();
                }}
              >
                ×
              </button>
            )}

            <div className="w-full">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-xl text-onPrimary">
                <HiOutlineVideoCamera />
              </span>
              <h1 className="mt-4 text-xl font-semibold text-appText">
                Confirm your identity
              </h1>
              <p className="mt-2 text-sm leading-6 text-appText/65">
                We&apos;ll take a few quick photos to verify it&apos;s you
                attending training. Your photos are used only for identity
                matching and aren&apos;t shared.
              </p>

              <div className="mt-4 rounded-xl border border-appText/10 bg-appBg p-3">
                <div className="relative flex h-[180px] items-center justify-center overflow-hidden rounded-lg bg-primary/15">
                  <video
                    ref={previewVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="absolute inset-0 z-10 h-full w-full scale-x-[-1] object-cover"
                  />
                  <svg
                    aria-hidden="true"
                    className="h-[90px] w-[72px] fill-cardBg"
                    viewBox="0 0 72 90"
                  >
                    <ellipse cx="36" cy="34" rx="22" ry="26" />
                    <path d="M10 88Q10 58 36 58Q62 58 62 88" />
                  </svg>
                  <span className="absolute left-2 top-2 z-20 inline-flex items-center gap-1.5 rounded-full bg-appText/70 px-2.5 py-1 text-[11px] font-medium text-cardBg">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                    LIVE
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <HiOutlineVideoCamera className="shrink-0 text-lg text-appText/45" />
                  <Select
                    aria-label="Select camera"
                    displayEmpty
                    disabled={isCheckingCameras || !cameras.length}
                    id="profile-landmark-camera"
                    MenuProps={appThemedSelectMenuProps}
                    sx={appThemedSelectSx}
                    value={selectedCamera}
                    className="w-full overflow-hidden whitespace-nowrap text-ellipsis bg-transparent text-sm text-appText"
                    onChange={(event) => setSelectedCamera(event.target.value)}
                  >
                    {cameras.map((camera) => (
                      <MenuItem key={camera.deviceId} value={camera.deviceId}>
                        {camera.label || "Default camera"}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>

              {isCheckingCameras ? (
                <p className="mt-3 text-sm text-appText/60">
                  Checking available cameras...
                </p>
              ) : cameraError ? (
                <div className="mt-3 flex items-center justify-between gap-3 text-sm text-red-500">
                  <span>{cameraError}</span>
                  <button
                    className="shrink-0 font-medium text-primary underline underline-offset-4"
                    onClick={() => setCameraCheck((current) => current + 1)}
                  >
                    Retry
                  </button>
                </div>
              ) : null}
            </div>

            <button
              className="mt-4 h-[42px] w-full rounded-xl bg-primary px-4 text-sm font-medium text-onPrimary transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-appBg disabled:text-appText/35"
              disabled={!selectedCamera || isCheckingCameras || !!cameraError}
              onClick={() => setHasStartedCapture(true)}
            >
              Next
            </button>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-dvh bg-appBg md:p-4">
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 md:backdrop-blur-sm">
        <section
          aria-label="Face capture"
          className="relative max-h-[calc(100dvh-2rem)] w-full max-w-[360px] overflow-y-auto rounded-2xl border border-appText/10 bg-cardBg p-6 shadow-[0_30px_70px_-24px_rgba(20,10,40,0.55)]"
        >
          {!required && (
            <button
              aria-label="Close face capture"
              className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-appBg/80 text-xl text-appText/60 transition hover:text-appText"
              onClick={() => {
                if (captureTransitionTimeoutRef.current) {
                  clearTimeout(captureTransitionTimeoutRef.current);
                  captureTransitionTimeoutRef.current = null;
                }
                const stream = videoRef.current
                  ?.srcObject as MediaStream | null;
                stream?.getTracks().forEach((track) => track.stop());
                if (videoRef.current) videoRef.current.srcObject = null;
                setIsModalOpen(false);
                onClose?.();
              }}
            >
              ×
            </button>
          )}

          <header>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-[0.08em] text-primary">
              Step {captureStep + 1} of {captureSteps.length}
            </p>
            <h1 className="text-xl font-semibold text-appText">
              {isComplete ? "All set" : currentStep.title}
            </h1>
            <div className="mt-3 flex gap-1">
              {captureSteps.map((_, index) => (
                <span
                  className={`h-1 flex-1 rounded-full ${captures[index] ? "bg-primary" : index === captureIndex ? "bg-primary/45" : "bg-appText/10"}`}
                  key={index}
                />
              ))}
            </div>
          </header>

          <div
            className={`relative mt-4 flex items-center justify-center overflow-hidden rounded-xl border border-appText/10 bg-appBg ${isComplete ? "h-[250px]" : "h-[300px]"}`}
            ref={viewportRef}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className={
                isComplete
                  ? "hidden"
                  : "absolute inset-0 h-full w-full scale-x-[-1] object-cover"
              }
            />
            {isComplete ? (
              <div className="text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-400 text-3xl font-bold text-emerald-950">
                  ✓
                </span>
                <h2 className="mt-5 text-lg font-semibold text-appText">
                  All images captured
                </h2>
                <p className="mt-2 text-sm text-appText/60">
                  Review or retake a photo below before continuing.
                </p>
              </div>
            ) : (
              <>
                {manualCapturedPose !== null ? (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-appBg/90 p-6 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-400 text-2xl font-bold text-emerald-950">
                      ✓
                    </span>
                    <h2 className="mt-3 text-lg font-semibold text-appText">
                      {captureSteps[manualCapturedPose].label} photo captured
                    </h2>
                    <p className="mt-1 text-sm text-appText/60">
                      {nextCaptureStep === null
                        ? "Preparing your photos..."
                        : `Next: ${captureSteps[nextCaptureStep].title}`}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="absolute top-3 z-10 flex items-center gap-1.5 rounded-full border border-appText/10 bg-cardBg px-3 py-1 text-xs font-medium text-appText/70">
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${isAligned ? "bg-emerald-400" : "bg-primary"}`}
                      />
                      {status}
                    </div>
                    <div
                      className={`relative z-10 aspect-[9/11] h-[220px] ${isAligned ? "[&>svg]:stroke-emerald-400" : "[&>svg]:stroke-appText/30"}`}
                    >
                      <svg
                        className="h-full w-full fill-none stroke-2 [stroke-dasharray:6_5]"
                        viewBox="0 0 220 290"
                      >
                        <ellipse cx="110" cy="145" rx="98" ry="133" />
                      </svg>
                      {isAligned && (
                        <span className="absolute bottom-2 left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-emerald-400 text-lg font-bold text-emerald-950">
                          ✓
                        </span>
                      )}
                    </div>
                    <div className="absolute bottom-3 z-10 flex items-center gap-1.5 rounded-full border border-appText/10 bg-cardBg px-3 py-1 text-xs font-medium text-appText/70">
                      {hint}
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <footer className="mt-4 space-y-3">
            {isManualCapture && !isComplete && (
              <button
                className="h-[42px] w-full rounded-xl border border-primary bg-primary/10 px-4 text-sm font-medium text-primary transition hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={
                  !videoRef.current?.videoWidth ||
                  isCapturingRef.current ||
                  manualCapturedPose !== null
                }
                onClick={captureCurrentFrame}
              >
                {manualCapturedPose === null
                  ? `Capture ${currentStep.label} photo`
                  : `${captureSteps[manualCapturedPose].label} captured`}
              </button>
            )}
            {visionError && !isComplete && (
              <p className="text-center text-xs font-medium text-appText/60">
                {visionError}
              </p>
            )}
            <div className="flex gap-2">
              {captureSteps.map((step, index) => (
                <div className="flex-1" key={step.label}>
                  <button
                    aria-label={`Retake ${step.label} photo`}
                    className={`flex h-[62px] w-full items-center justify-center overflow-hidden rounded-[10px] border bg-appBg disabled:cursor-default ${index === captureIndex && !isComplete ? "border-emerald-400" : "border-appText/10"}`}
                    disabled={!captures[index]}
                    onClick={() => retake(index)}
                  >
                    {captures[index] ? (
                      <img
                        alt={`${step.label} face capture`}
                        className="h-full w-full object-cover"
                        src={captures[index]}
                      />
                    ) : (
                      <span className="text-lg text-appText/35">◯</span>
                    )}
                  </button>
                  <span className="mt-1 block text-center text-[11px] font-medium text-appText/65">
                    {step.label}
                  </span>
                </div>
              ))}
            </div>
            <button
              className="h-[42px] w-full rounded-xl bg-primary px-4 text-sm font-medium text-onPrimary transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-appBg disabled:text-appText/35"
              disabled={!isComplete || isSubmitting}
              onClick={continueCapture}
            >
              {isSubmitting ? "Verifying..." : "Continue"}
            </button>
            {activeCaptureError && (
              <p className="text-center text-xs font-medium text-red-500">
                {activeCaptureError}
              </p>
            )}
            {!isManualCapture && !isComplete && (
              <button
                className="mx-auto block text-[11px] text-appText/35 hover:text-appText/55"
                onClick={startManualCapture}
              >
                Prefer to capture manually?
              </button>
            )}
          </footer>
        </section>
      </div>
    </main>
  );
}
